import { supabase } from '../supabase/supabase';

const callRpc = async (name, params) => {
  const { data, error } = await supabase.rpc(name, params);
  if (error) throw error;
  return data;
};

const rowsFrom = (data) => {
  if (!Array.isArray(data)) return data ? [data] : [];
  return data.map((row) => {
    if (typeof row === 'string') {
      try {
        return JSON.parse(row);
      } catch {
        return row;
      }
    }
    return row;
  });
};

const invokePlanFunction = async (name, body) => {
  const { data, error } = await supabase.functions.invoke(name, { body });

  if (error) {
    let message = error.message;
    try {
      const payload = await error.context?.clone?.().json();
      message = payload?.error || message;
    } catch {
      // Use the SDK message when the response has no JSON error body.
    }
    throw new Error(message || 'The seller plan request failed.');
  }

  if (data?.error) throw new Error(data.error);
  return data;
};

const synchronizePlanBilling = async (planId) => {
  if (!planId) return;
  try {
    await invokePlanFunction('admin-sync-seller-plan', { plan_id: planId });
  } catch (error) {
    throw new Error(
      `The plan was saved, but Stripe billing synchronization did not finish: ${error.message}`
    );
  }
};

export const fetchAdminMembershipPlans = async () => rowsFrom(
  await callRpc('admin_get_membership_plans_1793060000000')
);

export const saveMembershipPlan = async (plan) => {
  const saved = await callRpc(
    'admin_upsert_seller_membership_plan_1793065000000',
    {
      p_plan_id: plan.id || null,
      p_name: plan.name,
      p_slug: plan.slug,
      p_description: plan.description,
      p_monthly_price: Number(plan.monthly_price || 0),
      p_currency: plan.currency,
      p_features: plan.features,
      p_is_active: plan.is_active,
      p_plan_type: plan.plan_type || (plan.is_trial ? 'trial' : 'paid'),
      p_trial_duration_days: Number(plan.trial_duration_days),
      p_claim_interval_months: Number(plan.claim_interval_months)
    }
  );

  await synchronizePlanBilling(saved?.id);
  return saved;
};

export const saveMembershipPlanFeatures = (planId, features) => callRpc(
  'admin_update_seller_plan_features_1793068000000',
  {
    p_plan_id: planId,
    p_features: features
  }
);

export const archiveMembershipPlan = async (planId) => {
  const result = await callRpc(
    'admin_archive_seller_membership_plan_1793065000000',
    { p_plan_id: planId }
  );
  await synchronizePlanBilling(planId);
  return result;
};

export const fetchAdminPlanAssignments = async () => rowsFrom(
  await callRpc('admin_get_membership_assignments_1793060000000')
);

export const assignMembershipPlan = (userId, planId, durationMonths) =>
  callRpc('admin_assign_membership_plan_1793060000000', {
    p_user_id: userId,
    p_plan_id: planId,
    p_duration_months: Number(durationMonths)
  });

export const fetchMyPlanAccess = async () => {
  const result = await callRpc('membership_get_my_plan_access_1793060000000');
  if (typeof result === 'string') return JSON.parse(result);
  return result || { trial_plans: [], active_assignments: [] };
};

export const claimMembershipTrial = (planId) => callRpc(
  'membership_claim_trial_1793060000000',
  { p_plan_id: planId }
);

export const fetchSellerPlanAccess = async () => {
  const result = await callRpc('membership_get_seller_plan_access_1793065000000');
  if (typeof result === 'string') return JSON.parse(result);
  return result || null;
};

export const fetchSellerPlanChoices = async () => rowsFrom(
  await callRpc('membership_list_seller_plans_1793065000000')
);

export const claimSellerPlanTrial = (planId) => callRpc(
  'membership_claim_seller_trial_1793065000000',
  { p_plan_id: planId }
);

export const startSellerPlanCheckout = (planId) =>
  invokePlanFunction('seller-plan-checkout', { plan_id: planId });

export const confirmSellerPlanCheckout = (sessionId) =>
  invokePlanFunction('seller-plan-checkout', {
    action: 'confirm',
    session_id: sessionId
  });

export const hasSellerPlanFeature = (access, featureKey) => {
  const featureSlug = String(featureKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');

  const feature = Object.entries(access?.features || {}).find(([key]) => (
    key.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') === featureSlug
  ));

  return Boolean(
    feature
    && feature[1] !== false
    && String(feature[1]).toLowerCase() !== 'false'
  );
};
